/**
 * @copyright
 * Copyright(c) 2020 SICK AG
 */
import { createValueFormat } from './ValueFormat.js';
import { createTooltipBorderRadius, createTooltipFont, createTooltipPadding, createTooltipShadow } from './TooltipTools.js';
import { CssTools } from './CssTools.js';
import { resolveCssColor } from './ColorTools.js';
const placeHolder = '${value}';
const placeHolderLength = placeHolder.length;
/**
 * Formats a threshold using either a label or a template in combination with
 * the threshold's value.
 * The label takes precedence over the template.
 *
 * INTERNAL USE ONLY
 *
 * @param thresholdValue - the value of the threshold
 * @param horizontal - true = the threshold-line is drawn from left to right
 * @param valueFormat - the format to be used for the value of the threshold
 * @param thresholdLabel - a label
 * @param thresholdTemplate - a template for a label
 * @param locale - the locale to be used to format the value
 */

export function formatThreshold(thresholdValue, valueFormat, horizontal, thresholdLabel, thresholdTemplate, locale) {
  const formattedValue = valueFormat(thresholdValue, locale);

  if (thresholdLabel === undefined) {
    if (thresholdTemplate === undefined) {
      // user neither defined a label nor template -> just use the value
      return formattedValue;
    }

    const trimmedTemplate = thresholdTemplate.trim();

    if (trimmedTemplate.length === 0) {
      // user did not define a label but an empty template -> just use the value
      return formattedValue;
    }

    const index = trimmedTemplate.indexOf(placeHolder);

    if (index === -1) {
      // user does not want to see the value -> just use template
      return trimmedTemplate;
    }

    return `${trimmedTemplate.substring(0, index)}${formattedValue}${trimmedTemplate.substring(index + placeHolderLength)}`;
  }

  const trimmedLabel = thresholdLabel.trim();

  if (trimmedLabel.length === 0) {
    // user defined an empty label -> just use the value
    return formattedValue;
  } // append formatted value to the end of the trimmed label


  return `${trimmedLabel}${horizontal ? '\n' : ' '}${formattedValue}`;
}
/**
 * Describes a threshold
 */

/**
 * Computes all of the thresholds-related data to be fed into QuickChart.
 *
 * INTERNAL USE ONLY
 *
 * @param thresholds - the main data-model
 * @param horizontal - true = the threshold-line is drawn from left to right
 * @param fontFamily - the font-family to be used for the threshold-labels
 * @param shorthandLabels - whether the threshold-values should be formatted with shorthand-labels
 * @param decimalPlaces - how many decimal places to use when formatting the threshold-values
 * @param lineColor - which color to use for the threshold-line
 */
export function computeThresholds(thresholds, horizontal, fontFamily, shorthandLabels, decimalPlaces, lineColor) {
  if (thresholds.length === 0) {
    return [];
  }

  const valueFormat = createValueFormat(shorthandLabels, decimalPlaces);
  const activeColor = resolveCssColor('var(--davinci-color-background-elements-5)');
  const thresholdLineStyle = {
    color: resolveCssColor(lineColor || 'var(--davinci-color-background-elements-4)'),
    type: 'LargeDashes',
    width: 1.0
  };
  const thresholdLineStyleActive = { ...thresholdLineStyle,
    color: activeColor
  };
  const thresholdLabelFont = createTooltipFont(fontFamily);
  const labelBoxStyle = createThresholdBoxStyle(thresholdLineStyle.color);
  const labelBoxStyleActive = { ...labelBoxStyle,
    borderColor: activeColor,
    shadow: {
      color: 'rgba(0, 0, 0, 0.75)',
      offsetX: 2,
      offsetY: 2,
      blurRadius: 5
    }
  };
  const arrowHeadLength = CssTools.REM2PIXEL(0.75);
  const arrowHeadWidth = arrowHeadLength;
  return thresholds.map(threshold => {
    return {
      value: threshold.value,
      label: formatThreshold(threshold.value, valueFormat, horizontal, threshold.label, threshold.template),
      arrowHeadLength: arrowHeadLength,
      arrowHeadWidth: arrowHeadWidth,
      labelColor: thresholdLineStyle.color,
      labelColorActive: activeColor,
      labelFont: thresholdLabelFont,
      lineStyle: thresholdLineStyle,
      lineStyleActive: thresholdLineStyleActive,
      labelBoxStyle: labelBoxStyle,
      labelBoxStyleActive: labelBoxStyleActive
    };
  });
}

function createThresholdBoxStyle(lineColor) {
  return {
    backgroundColor: resolveCssColor('var(--davinci-color-background-input)'),
    borderColor: lineColor,
    color: lineColor,
    padding: createTooltipPadding(),
    borderRadius: createTooltipBorderRadius(),
    shadow: createTooltipShadow()
  };
}
